// src/pages/accounting/collateralReceived/hooks/useCollateralReceivedData.js
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { formatDateForApi } from '../../../../utils/dateUtils';
import { toastService } from '../../../../utils/toastService';
import { useGetCollateralReceivedListQuery, useDeleteCollateralReceivedMutation } from '../../../../api/endpoints/collateralReceived';

// docs/CollateralReceived-API.md §2: para birimi artık backend'den (kaldırılan /selected-types
// ucundan) değil, FE'de sabit listeden geliyor. Geçerlilik (`validity`) ise ekranda serbest metin
// alanı — sabit bir dropdown listesi yok (bkz. CollateralReceivedFilter/FormDialog).
const CURRENCY_CODES = ['TRY', 'EUR', 'USD'];

const emptyFilters = {
    sapDealerId: null,
    numbers: '',
    type: null,
    currency: null,
    validity: null,
    bankName: null,
    startDate: null,
    endDate: null
};

/**
 * Alınan Teminatlar liste ekranının tüm state + API mantığı (AGENTS.md: index.jsx yalnızca sunumdur).
 * Yetki kapsamı backend'de uygulanır (bkz. docs/CollateralReceived-API.md §4); Süper Admin (UpdateRole)/FinanceTeam
 * dışındaki kullanıcılar için bayi filtresi kendi bayisiyle sabitlenir (§`ownSapDealerId`).
 */
export default function useCollateralReceivedData() {
    const { th, getCurrentLanguageId } = useLanguage();
    const langId = getCurrentLanguageId();
    const { hasManagePermission, hasFinanceTeamRole } = usePermissions();
    const canBrowseAllDealers = hasManagePermission('UpdateRole') || hasFinanceTeamRole();
    const { sapDealerId: ownSapDealerId } = useSelector(selectUserParams);

    const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 10 });
    const [draftFilters, setDraftFilters] = useState(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

    // Not: banka ve teminat türü listeleri paylaşılan lookup ucundan geliyor
    // (bkz. CollateralBankDropdown.jsx — GroupCode=COLLATERAL_BANKS, CollateralTypeDropdown.jsx — GroupCode=COLLATERAL_TYPE).
    const options = useMemo(
        () => ({
            currencies: CURRENCY_CODES.map((c) => ({ value: c, label: c }))
        }),
        []
    );

    const setDraftField = useCallback((field, value) => {
        setDraftFilters((prev) => ({ ...prev, [field]: value }));
    }, []);

    const queryParams = useMemo(
        () => ({
            sapDealerId: canBrowseAllDealers ? appliedFilters.sapDealerId || undefined : ownSapDealerId || undefined,
            numbers: appliedFilters.numbers?.trim() || undefined,
            type: appliedFilters.type ?? undefined,
            currency: appliedFilters.currency || undefined,
            validity: appliedFilters.validity || undefined,
            bankName: appliedFilters.bankName || undefined,
            startDate: formatDateForApi(appliedFilters.startDate) || undefined,
            endDate: formatDateForApi(appliedFilters.endDate) || undefined,
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
            languageId: langId ?? 2
        }),
        [appliedFilters, pagination, langId, canBrowseAllDealers, ownSapDealerId]
    );
    const exportParams = useMemo(
        () => ({
            sapDealerId: canBrowseAllDealers ? appliedFilters.sapDealerId || undefined : ownSapDealerId || undefined,
            numbers: appliedFilters.numbers?.trim() || undefined,
            type: appliedFilters.type ?? undefined,
            currency: appliedFilters.currency || undefined,
            validity: appliedFilters.validity || undefined,
            bankName: appliedFilters.bankName || undefined,
            startDate: formatDateForApi(appliedFilters.startDate) || undefined,
            endDate: formatDateForApi(appliedFilters.endDate) || undefined,
            languageId: langId ?? 2
        }),
        [appliedFilters, langId, canBrowseAllDealers, ownSapDealerId]
    );

    const { data, isFetching, isLoading } = useGetCollateralReceivedListQuery(queryParams, { refetchOnMountOrArgChange: true });

    const rows = data?.items ?? [];
    const totalCount = data?.totalCount ?? 0;
    const loading = isLoading || isFetching;

    const onPageChange = useCallback((e) => {
        const pageNumber = Math.floor(e.first / e.rows) + 1;
        setPagination({ pageNumber, pageSize: e.rows });
    }, []);

    const handleApplyFilters = useCallback(() => {
        setAppliedFilters(draftFilters);
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, [draftFilters]);

    const handleResetFilters = useCallback(() => {
        setDraftFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const [deleteCollateralReceived] = useDeleteCollateralReceivedMutation();

    const handleDelete = useCallback(
        (row) => {
            confirmDialog({
                message: th('collateralReceived.deleteConfirmMessage'),
                header: th('collateralReceived.deleteConfirmHeader'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: th('common.yes'),
                rejectLabel: th('common.no'),
                rejectClassName: 'p-button-outlined',
                accept: async () => {
                    try {
                        await deleteCollateralReceived(row.id).unwrap();
                        toastService.success(th('collateralReceived.deleteSuccess'), th('common.success'));
                    } catch (error) {
                        toastService.error(error?.data?.errorMessage || th('collateralReceived.deleteError'), th('common.error'));
                    }
                }
            });
        },
        [deleteCollateralReceived, th]
    );

    return {
        rows,
        loading,
        totalCount,
        pagination,
        onPageChange,

        draftFilters,
        setDraftField,
        canBrowseAllDealers,
        options,
        exportParams,
        handleApplyFilters,
        handleResetFilters,
        handleDelete
    };
}
